import React, { useState, useEffect } from "react";
import {
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  Input,
  Select,
  useToast,
  useDisclosure, // ✅ added
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormLabel,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { Label } from "recharts";

export const ChatList = () => {
  const [chats, setChats] = useState([]);
  const [filteredChats, setFilteredChats] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [userId, setUserId] = useState(""); 
  const [startDate, setStartDate] = useState(""); 
  const [endDate, setEndDate] = useState(""); 
  const [skip, setSkip] = useState(0); 
  const [limit, setLimit] = useState(20); 
  const [total, setTotal] = useState(0); 
  const [error, setError] = useState("");
  const [selectedChat, setSelectedChat] = useState(null); // ✅ added
  const { isOpen, onOpen, onClose } = useDisclosure(); // ✅ added
  const toast = useToast();

  const fetchChats = async () => {
    try {
      const params = new URLSearchParams();

      if (userId) params.append("user_id", userId);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      params.append("skip", skip);
      params.append("limit", limit);

      const url = `${process.env.REACT_APP_API_URL3}/chats?${params.toString()}`;
      console.log("Chats Request URL:", url);

      const response = await fetchWithToken(url);
      if (!response) {
        throw new Error("Failed to fetch chats");
      }

      setChats(response.chats);
      setFilteredChats(response.chats);
      setTotal(response.total || 0);
    } catch (error) {
      console.error("Error fetching chats:", error);
      setError("Failed to fetch chats");
    }
  };

  useEffect(() => {
    fetchChats();
  }, [userId, startDate, endDate, skip, limit]);

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(event.target.value);
  
    const filtered = chats.filter((chat) =>
      chat.username?.toLowerCase().includes(query) ||
      chat._id?.toLowerCase().includes(query)
    );
  
    setFilteredChats(filtered);
  };
  

  const handleNextPage = () => {
    setSkip(skip + limit);
  };

  const handlePreviousPage = () => {
    if (skip >= limit) {
      setSkip(skip - limit);
    }
  };

  const getLastMessage = (messages) => {
    if (!messages || messages.length === 0) return "No messages";
    return messages[messages.length - 1]?.content || "No content";
  };

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }}>
      <Flex px="25px" justify="space-between" mb="20px" align="center" wrap="wrap" gap="20px">
        <Text color="gray.800" fontSize="22px" fontWeight="700" lineHeight="100%" flexBasis="100%">
          Chat List
        </Text>
        <Flex gap="10px" flexWrap="wrap" justify="space-between" w="100%">
          <Input
            placeholder="Search by username or user ID..."
            value={searchQuery}
            onChange={handleSearch}
            size="sm"
            w={{ base: "100%", md: "200px" }}
          />
          <FormLabel fontSize="sm">Start Date</FormLabel>
          <Input
            type="date"
            placeholder="Start date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            size="sm"
            w={{ base: "100%", md: "200px" }}
          />
          <FormLabel fontSize="sm">End Date</FormLabel>
          <Input
            type="date"
            placeholder="End date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            size="sm"
            w={{ base: "100%", md: "200px" }}
          />
          <Select
            placeholder="Select limit"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            size="sm"
            w={{ base: "100%", md: "120px" }}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>
        </Flex>
      </Flex>

      <Box overflowY="auto" maxHeight="400px">
        <Table variant="simple" color="gray.500" mb="24px">
          <Thead>
            <Tr bg="rgb(69, 50, 240)">
              <Th textColor="white">Index</Th>
              <Th textColor="white">user ID</Th>
              <Th textColor="white">Username</Th>
              <Th textColor="white">Last Message</Th>
              <Th textColor="white">Last Active</Th>
              <Th textColor="white">Total Messages</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredChats.map((chat, index) => (
              <Tr
                key={chat._id}
                onClick={() => { setSelectedChat(chat); onOpen(); }} // ✅ added click
                cursor="pointer" // ✅ added pointer
                _hover={{ bg: "gray.100" }} // ✅ hover effect
              >
                <Td>{index + 1}</Td>
                <Td>{chat._id || "N/A"}</Td>
                <Td>{chat.username || "N/A"}</Td>
                <Td>{getLastMessage(chat.messages)}</Td>
                <Td>
                  {chat.last_active
                    ? new Intl.DateTimeFormat("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      }).format(new Date(chat.last_active))
                    : "N/A"}
                </Td>
                <Td>{chat.messages?.length || 0}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Flex justify="space-between" px="25px" mt="10px">
        <Button
          size="sm"
          onClick={handlePreviousPage}
          isDisabled={skip === 0}
          colorScheme="blue"
        >
          Previous
        </Button>
        <Button
          size="sm"
          onClick={handleNextPage}
          isDisabled={total === 0 || skip + limit >= total}
          colorScheme="blue"
        >
          Next
        </Button>
      </Flex>

      {error && (
        <Box mt="4">
          <Text color="red.500">{error}</Text>
        </Box>
      )}

      {/* ✅ Modal for chat view */}
      {selectedChat && (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Chat with {selectedChat.username}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text>User ID : {selectedChat._id}</Text>
              <Flex direction="column" gap="4">
                {selectedChat.messages?.length > 0 ? (
                  selectedChat.messages.map((msg, idx) => (
                    <Flex
                      key={idx}
                      justify={msg.role === "user" ? "flex-end" : "flex-start"}
                    >
                      <Box
                        maxW="70%"
                        p="3"
                        borderRadius="md"
                        bg={msg.role === "user" ? "blue.100" : "gray.100"}
                        color="black"
                        boxShadow="md"
                      >
                        <Text mb="1" fontSize="sm" color="gray.600">
                          {msg.role === "user" ? selectedChat.username : "Assistant"}
                        </Text>
                        <Text>{msg.content}</Text>
                        <Text fontSize="xs" color="gray.500" textAlign="right" mt="2">
                          {msg.timestamp
                            ? new Intl.DateTimeFormat("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }).format(new Date(msg.timestamp))
                            : ""}
                        </Text>
                      </Box>
                    </Flex>
                  ))
                ) : (
                  <Text>No messages available.</Text>
                )}
              </Flex>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={onClose}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

    </Card>
  );
};
